// src/services/sseClient.ts
//
// Minimal fetch + ReadableStream Server-Sent Events client. EventSource can't POST
// or send custom headers, so we parse `text/event-stream` ourselves. Auth matches
// the axios client in api.js (X-API-Key + Supabase Bearer, one refresh on 401).
import API_CONFIG from "../config/api.config";
import { ensureFreshSession, getAuthHeaders, handleSessionExpired } from "./api";

export interface SSEMessage {
  /** `event:` field; "message" when the server didn't name it. */
  event: string;
  /** All `data:` lines of the event, joined with "\n". */
  data: string;
  id?: string;
}

export interface SSEParser {
  /** Feed a decoded text chunk. Chunks may split anywhere — mid-line or mid-"\r\n". */
  feed(chunk: string): void;
  /** End of stream: dispatch a trailing event that lacked its final blank line. */
  flush(): void;
}

/**
 * Incremental parser for the `text/event-stream` format (WHATWG HTML §9.2.6):
 * `\n`, `\r` and `\r\n` line endings, multi-line `data:`, `:` comments, and one
 * optional space after the colon.
 */
export function createSSEParser(onMessage: (message: SSEMessage) => void): SSEParser {
  let buffer = "";
  // A chunk ended on "\r": if the next chunk starts with "\n" it's the same line break.
  let skipLeadingLF = false;
  let eventName = "";
  let dataLines: string[] = [];
  let lastId: string | undefined;

  const dispatch = () => {
    if (dataLines.length > 0) {
      onMessage({ event: eventName || "message", data: dataLines.join("\n"), id: lastId });
    }
    eventName = "";
    dataLines = [];
  };

  const processLine = (line: string) => {
    if (line === "") {
      dispatch();
      return;
    }
    if (line.startsWith(":")) return; // comment / keep-alive
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);

    switch (field) {
      case "event":
        eventName = value;
        break;
      case "data":
        dataLines.push(value);
        break;
      case "id":
        if (!value.includes("\0")) lastId = value;
        break;
      default:
        break; // `retry` and unknown fields are irrelevant for a one-shot POST stream
    }
  };

  return {
    feed(chunk) {
      if (skipLeadingLF && chunk.startsWith("\n")) chunk = chunk.slice(1);
      skipLeadingLF = false;
      buffer += chunk;

      let start = 0;
      for (let i = 0; i < buffer.length; i++) {
        const ch = buffer[i];
        if (ch !== "\n" && ch !== "\r") continue;
        processLine(buffer.slice(start, i));
        if (ch === "\r") {
          if (i + 1 < buffer.length) {
            if (buffer[i + 1] === "\n") i++;
          } else {
            skipLeadingLF = true;
          }
        }
        start = i + 1;
      }
      buffer = buffer.slice(start);
    },
    flush() {
      if (buffer !== "") {
        processLine(buffer);
        buffer = "";
      }
      dispatch();
    },
  };
}

/** The stream endpoint is missing/unsupported (404/405/501, network, not an event
 *  stream). Thrown only before any event arrived, so callers can fall back. */
export class SSEUnavailableError extends Error {
  readonly status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = "SSEUnavailableError";
    this.status = status;
  }
}

/** Any other non-2xx response before the stream started. */
export class SSEHttpError extends Error {
  readonly status: number;
  readonly body: string;
  constructor(status: number, body: string) {
    super(`Stream request failed with status ${status}`);
    this.name = "SSEHttpError";
    this.status = status;
    this.body = body;
  }
}

const UNAVAILABLE_STATUSES = new Set([404, 405, 501]);

export type SSEHandlers<TEvents> = {
  [K in keyof TEvents]?: (data: TEvents[K]) => void;
};

export interface StreamSSEOptions<TEvents> {
  /** Path relative to API_CONFIG.BASE_URL, e.g. RECON.reconSuggestionsByKeysStream. */
  path: string;
  /** JSON request body (sent as POST). */
  body: unknown;
  /** One typed callback per event name. Events without a handler are ignored. */
  handlers: SSEHandlers<TEvents>;
  signal?: AbortSignal;
  /** Called once with the first parsed event — the point after which no fallback is possible. */
  onOpen?: () => void;
}

export const isAbortError = (error: unknown): boolean =>
  (error instanceof DOMException || error instanceof Error) &&
  (error.name === "AbortError" || error.name === "CanceledError");

async function postStream(path: string, body: unknown, token: string | null, signal?: AbortSignal) {
  const headers = {
    ...((await getAuthHeaders(token)) as Record<string, string>),
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  try {
    return await fetch(`${API_CONFIG.BASE_URL}${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new SSEUnavailableError(error instanceof Error ? error.message : "Network error");
  }
}

/**
 * POST `body` to `path` and dispatch each SSE event's JSON `data` to the typed
 * handler for its name. Resolves when the server closes the stream.
 */
export async function streamSSE<TEvents extends object>({
  path,
  body,
  handlers,
  signal,
  onOpen,
}: StreamSSEOptions<TEvents>): Promise<void> {
  let response = await postStream(path, body, null, signal);

  if (response.status === 401) {
    // Same policy as the axios interceptor: refresh once, then give up.
    let freshToken: string;
    try {
      freshToken = await ensureFreshSession();
    } catch {
      await handleSessionExpired();
      throw new SSEHttpError(401, "");
    }
    response = await postStream(path, body, freshToken, signal);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    if (UNAVAILABLE_STATUSES.has(response.status)) {
      throw new SSEUnavailableError(`Stream endpoint unavailable (${response.status})`, response.status);
    }
    throw new SSEHttpError(response.status, text);
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("text/event-stream") || !response.body) {
    // Don't await: some bodies never settle a cancel; we just drop the response.
    response.body?.cancel().catch(() => undefined);
    throw new SSEUnavailableError(`Expected text/event-stream, got "${contentType}"`);
  }

  let opened = false;
  const parser = createSSEParser(({ event, data }) => {
    if (!opened) {
      opened = true;
      onOpen?.();
    }
    const handler = (handlers as Record<string, ((payload: unknown) => void) | undefined>)[event];
    if (!handler) return; // unknown events are ignored by contract
    let payload: unknown;
    try {
      payload = data === "" ? {} : JSON.parse(data);
    } catch {
      console.warn(`Ignoring SSE "${event}" with malformed JSON`, data);
      return;
    }
    handler(payload);
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const throwIfAborted = () => {
    if (signal?.aborted) throw new DOMException("The operation was aborted.", "AbortError");
  };
  // Not every fetch implementation errors a pending read() on abort — cancel it ourselves.
  const onAbort = () => {
    reader.cancel().catch(() => undefined);
  };
  signal?.addEventListener("abort", onAbort, { once: true });
  try {
    for (;;) {
      const { done, value } = await reader.read();
      throwIfAborted();
      if (done) break;
      parser.feed(decoder.decode(value, { stream: true }));
    }
    parser.feed(decoder.decode());
    parser.flush();
  } catch (error) {
    if (isAbortError(error) || signal?.aborted) {
      throw new DOMException("The operation was aborted.", "AbortError");
    }
    // Before the first event a broken stream is just as good as "unavailable".
    if (!opened) {
      throw new SSEUnavailableError(error instanceof Error ? error.message : "Stream failed");
    }
    throw error;
  } finally {
    signal?.removeEventListener("abort", onAbort);
    reader.releaseLock();
  }
}
